import React from 'react';

const guideStyle: React.CSSProperties = {
  maxWidth: 800,
  margin: '40px auto',
  background: 'rgba(255, 255, 255, 0.5)',
  borderRadius: 12,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  padding: '32px 28px',
  fontSize: '1.08em',
  lineHeight: 1.7,
  color: '#222',
};

const sectionTitle: React.CSSProperties = {
  color: '#00aeff',
  fontWeight: 700,
  marginTop: 32,
  marginBottom: 12,
  fontSize: '1.25em',
  borderBottom: '1.5px solid #e3e3e3',
  paddingBottom: 4,
};

const highlight: React.CSSProperties = {
  background: '#e3f2fd',
  borderRadius: 4,
  padding: '2px 6px',
  fontWeight: 500,
};

const ulStyle: React.CSSProperties = {
  paddingLeft: 40, // More indentation for bullet points
  marginBottom: 0,
};

const Guide: React.FC = () => (
  <>
    <style>
      {`
        @media (max-width: 600px) {
          .guide-root {
            padding: 12px 5vw !important;
            font-size: 1em !important;
            border-radius: 12 !important;
            box-shadow: none !important;
            margin: 0 !important;
            max-width: 100vw !important;
          }
          .guide-root h1 {
            font-size: 1.2em !important;
            margin-bottom: 12px !important;
          }
          .guide-section-title {
            font-size: 1.05em !important;
            margin-top: 22px !important;
            margin-bottom: 8px !important;
            padding-bottom: 2px !important;
          }
          .guide-root ul {
            padding-left: 20px !important;
          }
        }
      `}
    </style>
    <div className="guide-root" style={guideStyle}>
      <h1 style={{ color: '#00aeff', marginBottom: 18 }}>Rental Management Guide & FAQ</h1>
      <p>Welcome! This guide explains how to use the Rental Management System and clarifies key features and workflows.</p>

      <div className="guide-section-title" style={sectionTitle}>Auto Bill Generation</div>
      <ul style={ulStyle}>
        <li>
          Bills are <span style={highlight}>automatically generated</span> for all active tenants at the start of each month.
        </li>
        <li>
          Each bill is linked to a tenant and their property, and includes the planned rent and due date.
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Lease Ending Alerts</div>
      <ul style={ulStyle}>
        <li>
          If a tenant's lease is ending within <span style={highlight}>30 days</span>, a lease ending alert will appear on the dashboard and in the tenant list.
        </li>
        <li>
          The <span style={highlight}>Deboard/Settlement</span> option will be available for such tenants, even if they have no pending bills.
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Deboarding & Settlement Eligibility</div>
      <ul style={ulStyle}>
        <li>
          A tenant is eligible for deboarding/settlement if:
          <ul style={ulStyle}>
            <li>They have <span style={highlight}>pending bills</span> (Unpaid/Partial), <b>or</b></li>
            <li>Their lease is ending within 30 days.</li>
          </ul>
        </li>
        <li>
          The <span style={highlight}>Deboard</span> button appears in the tenant list when eligible.
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Tenant Documents</div>
      <ul style={ulStyle}>
        <li>
          You can <span style={highlight}>upload and manage documents</span> (Aadhar, PAN, Rental Agreement, Police Verification, etc.) for each tenant from their profile and edit page.
        </li>
        <li>
          Documents are securely stored and can be viewed or deleted anytime.
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Deboarding/Settlement with Pending Bills</div>
      <ul style={ulStyle}>
        <li>
          When deboarding a tenant with pending bills:
          <ul style={ulStyle}>
            <li>All pending bills are <span style={highlight}>marked as paid</span> (even if not fully paid).</li>
            <li>
              If the <span style={highlight}>additional payment</span> is less than the total due, the remaining amount is recorded as a <b>loss</b> in the settlement notes and outstanding.
            </li>
            <li>
              The tenant's status is set to <span style={highlight}>Inactive</span> and the property is marked as <span style={highlight}>Vacant</span>.
            </li>
          </ul>
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Viewing Tenant Bills & Payments</div>
      <ul style={ulStyle}>
        <li>
          Go to a tenant's profile to see all their <span style={highlight}>bills</span> and <span style={highlight}>payments</span> in dedicated tabs/sections.
        </li>
        <li>
          Each bill and payment can be clicked for detailed information.
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Bill & Payment Details</div>
      <ul style={ulStyle}>
        <li>
          On the <span style={highlight}>Bills</span> page, click any bill to view its details, including payment history and tenant/property info.
        </li>
        <li>
          On the <span style={highlight}>Payments</span> page, click any payment to view its details, including linked bill, tenant, and property.
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Property Details</div>
      <ul style={ulStyle}>
        <li>
          Click any property in the <span style={highlight}>Properties</span> page to view full details, including amenities, notes, maintenance records, and current tenant (if any).
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Tenant Details</div>
      <ul style={ulStyle}>
        <li>
          Click any tenant in the <span style={highlight}>Tenants</span> page to view their profile, lease info, bills, payments, and uploaded documents.
        </li>
      </ul>

      <div className="guide-section-title" style={sectionTitle}>Other Features & Notes</div>
      <ul style={ulStyle}>
        <li>
          <b>Outstanding Settlements:</b> Recent settlements with outstanding amounts are shown on the dashboard.
        </li>
        <li>
          <b>Alerts:</b> Payment and lease alerts are shown on the dashboard for quick action.
        </li>
      </ul>
      <div style={{ marginTop: 36, color: '#888', fontSize: '0.95em' }}>
        If you have more questions, please contact your administrator or support team.
      </div>
    </div>
  </>
);

export default Guide;
